import type { KpiDto, KpiValueDto } from "../dtos/KpiDto.js";
import { getSupabaseClient } from "@/core/supabase/client.js";
import { fetchAllPaged } from "@/core/supabase/pagination.js";

const KPI_FIELDS = `id, org_id, project_id, name, unit, description, project_value, session_value, province_value, outlet_value, category_id, sort_order, created_at, updated_at, created_by`;
const VALUE_FIELDS = `id, org_id, project_id, kpi_id, manual_value, system_value, location_id, session_id, submission_id, created_at, created_by`;
const VALUE_FIELDS_EXTENDED = `${VALUE_FIELDS}, location:locations!location_id(id, name), submitter:profiles!created_by(id, first_name, last_name, email)`;

export class KpiRemoteDataSource {
  private get supabase() {
    return getSupabaseClient() as any;
  }

  async getKpiById(id: string): Promise<KpiDto | null> {
    const { data, error } = await this.supabase
      .from("kpis")
      .select(KPI_FIELDS)
      .eq("id", id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async listKpis(options?: {
    orgId?: string;
    projectId?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ data: KpiDto[]; total: number }> {
    let query = this.supabase
      .from("kpis")
      .select(KPI_FIELDS, { count: "exact" })
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: true });
    if (options?.orgId) query = query.eq("org_id", options.orgId);
    if (options?.projectId) query = query.eq("project_id", options.projectId);
    if (
      typeof options?.limit === "number" &&
      typeof options?.offset === "number"
    ) {
      query = query.range(options.offset, options.offset + options.limit - 1);
    } else if (typeof options?.limit === "number") {
      query = query.limit(options.limit);
    }
    const { data, error, count } = await query;
    if (error) throw new Error(error.message);
    return { data: data ?? [], total: count ?? 0 };
  }

  async createKpi(
    payload: Partial<KpiDto> & {
      org_id: string;
      project_id: string;
      name: string;
      unit: string;
      project_value: number;
      session_value?: number | null;
      province_value?: number | null;
      outlet_value?: number | null;
      created_by: string;
    },
  ): Promise<KpiDto | null> {
    const { data, error } = await this.supabase
      .from("kpis")
      .insert(payload)
      .select(KPI_FIELDS)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async updateKpi(
    id: string,
    payload: Partial<KpiDto>,
  ): Promise<KpiDto | null> {
    const { data, error } = await this.supabase
      .from("kpis")
      .update(payload)
      .eq("id", id)
      .select(KPI_FIELDS)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async deleteKpi(id: string): Promise<void> {
    const { error } = await this.supabase.from("kpis").delete().eq("id", id);
    if (error) throw new Error(error.message);
  }

  async reorderKpis(updates: Array<{ id: string; sortOrder: number }>): Promise<void> {
    const { error } = await this.supabase.rpc("reorder_kpis", {
      p_updates: updates.map((u) => ({ id: u.id, sort_order: u.sortOrder })),
    });
    if (error) throw new Error(error.message);
  }

  async listKpiValues(kpiId: string): Promise<KpiValueDto[]> {
    return fetchAllPaged<KpiValueDto>((from, to) =>
      this.supabase
        .from("kpi_values")
        .select(VALUE_FIELDS_EXTENDED)
        .eq("kpi_id", kpiId)
        .order("created_at", { ascending: false })
        .order("id", { ascending: false })
        .range(from, to),
    );
  }

  async updateKpiValue(
    id: string,
    payload: {
      manual_value: number;
      location_id?: string | null;
      session_id?: string | null;
    },
  ): Promise<KpiValueDto> {
    const { data, error } = await this.supabase
      .from("kpi_values")
      .update(payload)
      .eq("id", id)
      .select(VALUE_FIELDS)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) throw new Error("KPI value update failed");
    return data;
  }

  async deleteKpiValue(id: string): Promise<void> {
    const { error } = await this.supabase
      .from("kpi_values")
      .delete()
      .eq("id", id);
    if (error) throw new Error(error.message);
  }

  async createKpiValue(payload: {
    org_id: string;
    project_id: string;
    kpi_id: string;
    manual_value: number;
    created_by: string;
    location_id?: string | null;
    session_id?: string | null;
    submission_id?: string;
  }): Promise<KpiValueDto> {
    const { data, error } = await this.supabase
      .from("kpi_values")
      .insert(payload)
      .select(VALUE_FIELDS)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) throw new Error("KPI value creation failed");
    return data;
  }

  async listKpiValuesByProject(
    orgId: string,
    projectId: string,
    filters?: {
      locationId?: string;
      createdBy?: string;
      dateFrom?: string;
      dateTo?: string;
    },
  ): Promise<KpiValueDto[]> {
    return fetchAllPaged<KpiValueDto>((from, to) => {
      let query = this.supabase
        .from("kpi_values")
        .select(VALUE_FIELDS_EXTENDED)
        .eq("org_id", orgId)
        .eq("project_id", projectId);
      if (filters?.locationId)
        query = query.eq("location_id", filters.locationId);
      if (filters?.createdBy) query = query.eq("created_by", filters.createdBy);
      if (filters?.dateFrom) query = query.gte("created_at", filters.dateFrom);
      if (filters?.dateTo) query = query.lte("created_at", filters.dateTo);
      return query
        .order("created_at", { ascending: false })
        .order("id", { ascending: false })
        .range(from, to);
    });
  }

  async listProjectProgress(
    orgId: string,
    projectIds: string[],
  ): Promise<Array<{ project_id: string; progress_pct: number }>> {
    if (!projectIds.length) return [];
    const { data, error } = await this.supabase.rpc("get_projects_kpi_progress", {
      p_org_id: orgId,
      p_project_ids: projectIds,
    });
    if (error) throw new Error(error.message);
    return data ?? [];
  }
}
